import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import InstallPrompt from "@/components/InstallPrompt";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import BookEditor from "@/pages/BookEditor";
import AdminDashboard from "@/pages/AdminDashboard";
import PublicBook from "@/pages/PublicBook";
import PublicAuthor from "@/pages/PublicAuthor";
import InviteLanding from "@/pages/InviteLanding";
import PublisherDashboard from "@/pages/PublisherDashboard";
import PublishersDirectory from "@/pages/PublishersDirectory";

// Reserved top-level paths that must NOT be treated as author slugs
const RESERVED = new Set([
  "", "login", "register", "dashboard", "admin", "books",
  "auth", "api", "public", "static", "settings", "logout", "invites",
  "publisher", "publishers",
]);

function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm" data-testid="protected-loading">Loading…</div>;
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  if (role === "admin" && user.role !== "admin") {
    return <Navigate to="/dashboard" replace />;
  }
  if (role === "publisher" && user.role !== "publisher") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function PublicSlugRoute() {
  // Render PublicAuthor for /:authorSlug if not reserved
  const path = window.location.pathname.split("/").filter(Boolean);
  if (path.length === 1 && RESERVED.has(path[0])) {
    return <Navigate to="/" replace />;
  }
  return <PublicAuthor />;
}

function AppRouter() {
  const location = useLocation();
  // OAuth session_id is delivered in URL fragment; handle it before normal routes
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/publisher" element={<ProtectedRoute role="publisher"><PublisherDashboard /></ProtectedRoute>} />
      <Route path="/publishers" element={<PublishersDirectory />} />
      <Route path="/books/:bookId" element={<ProtectedRoute><BookEditor /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
      <Route path="/invites/:token" element={<InviteLanding />} />
      <Route path="/:authorSlug/:bookSlug" element={<PublicBook />} />
      <Route path="/:authorSlug" element={<PublicSlugRoute />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors />
          <AppRouter />
          <InstallPrompt />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
